import { useState, useEffect, useRef } from 'react';

interface AIMascotProps {
  className?: string;
  showRatings?: boolean;
}

export default function AIMascot({ className = '', showRatings = true }: AIMascotProps) {
  const [floatOffset, setFloatOffset] = useState(0);
  const [rotateOffset, setRotateOffset] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [eyePosition, setEyePosition] = useState({ x: 0, y: 0 });
  const [blinkState, setBlinkState] = useState(false);
  const [glowIntensity, setGlowIntensity] = useState(1);
  const [particlePhase, setParticlePhase] = useState(0);
  const [wandAngle, setWandAngle] = useState(0);
  const [sparkles, setSparkles] = useState<Array<{id: number, x: number, y: number, opacity: number, scale: number}>>([]);
  const mascotRef = useRef<HTMLDivElement>(null);
  const sparkleIdRef = useRef(0);

  // Smooth floating and gentle rotation animation
  useEffect(() => {
    let animationFrame: number;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      setFloatOffset(Math.sin(elapsed / 1500) * 15);
      setRotateOffset(Math.sin(elapsed / 2000) * 2);
      setGlowIntensity(0.8 + Math.sin(elapsed / 1000) * 0.2);
      setParticlePhase(elapsed / 50);

      // Magic wand arc motion - continuous smooth arc
      const wandCycle = (elapsed % 3000) / 3000; // 3 second cycle
      const wandArc = Math.sin(wandCycle * Math.PI * 2) * 35; // Arc from -35 to +35 degrees
      setWandAngle(wandArc);

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  // Generate sparkles periodically
  useEffect(() => {
    const generateSparkle = () => {
      const id = sparkleIdRef.current++;
      const angle = (wandAngle + 45) * (Math.PI / 180);
      const wandTipX = 280 + Math.cos(angle) * 60;
      const wandTipY = 240 + Math.sin(angle) * 60;

      // Random offset from wand tip
      const offsetX = (Math.random() - 0.5) * 40;
      const offsetY = (Math.random() - 0.5) * 40;

      setSparkles(prev => [...prev, {
        id,
        x: wandTipX + offsetX,
        y: wandTipY + offsetY,
        opacity: 1,
        scale: 0.5 + Math.random() * 0.5
      }]);

      // Remove sparkle after animation
      setTimeout(() => {
        setSparkles(prev => prev.filter(s => s.id !== id));
      }, 1000);
    };

    const interval = setInterval(generateSparkle, 150);
    return () => clearInterval(interval);
  }, [wandAngle]);

  // Natural blinking
  useEffect(() => {
    const blink = () => {
      setBlinkState(true);
      setTimeout(() => setBlinkState(false), 120);
    };

    const interval = setInterval(() => {
      blink();
      if (Math.random() > 0.7) {
        setTimeout(blink, 200);
      }
    }, 2500 + Math.random() * 2000);

    return () => clearInterval(interval);
  }, []);

  // Track mouse for eye movement
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (mascotRef.current) {
        const rect = mascotRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const deltaX = (e.clientX - centerX) / 60;
        const deltaY = (e.clientY - centerY) / 60;

        setEyePosition({
          x: Math.max(-8, Math.min(8, deltaX)),
          y: Math.max(-6, Math.min(6, deltaY)),
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Customer avatars for ratings
  const customerAvatars = [
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
  ];

  // Floating particles data
  const particles = [
    { size: 8, x: -60, y: -80, delay: 0, color: '#a855f7' },
    { size: 6, x: 70, y: -60, delay: 0.5, color: '#ec4899' },
    { size: 10, x: -80, y: 20, delay: 1, color: '#3b82f6' },
    { size: 5, x: 90, y: 40, delay: 1.5, color: '#8b5cf6' },
    { size: 7, x: -40, y: 80, delay: 2, color: '#f472b6' },
    { size: 9, x: 50, y: 90, delay: 2.5, color: '#60a5fa' },
  ];

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* Mascot Container */}
      <div
        ref={mascotRef}
        className="relative cursor-pointer select-none"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          transform: `translateY(${floatOffset}px) rotate(${rotateOffset}deg)`,
          transition: 'transform 0.1s ease-out',
        }}
      >
        {/* Floating particles */}
        {particles.map((particle, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: particle.size,
              height: particle.size,
              left: `calc(50% + ${particle.x}px)`,
              top: `calc(50% + ${particle.y + Math.sin((particlePhase + particle.delay * 60) / 30) * 20}px)`,
              background: particle.color,
              opacity: 0.6 + Math.sin((particlePhase + particle.delay * 60) / 20) * 0.3,
              boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
              transition: 'opacity 0.3s',
            }}
          />
        ))}

        {/* Magic Sparkles from wand */}
        {sparkles.map((sparkle) => (
          <div
            key={sparkle.id}
            className="absolute pointer-events-none"
            style={{
              left: sparkle.x,
              top: sparkle.y,
              animation: 'sparkle-float 1s ease-out forwards',
            }}
          >
            <svg
              width={20 * sparkle.scale}
              height={20 * sparkle.scale}
              viewBox="0 0 20 20"
              style={{
                filter: 'drop-shadow(0 0 4px #fbbf24)',
              }}
            >
              <path
                d="M10 0 L11.5 8.5 L20 10 L11.5 11.5 L10 20 L8.5 11.5 L0 10 L8.5 8.5 Z"
                fill="#fbbf24"
              />
            </svg>
          </div>
        ))}

        {/* Main mascot */}
        <div
          className={`relative transition-transform duration-300 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}
          style={{ width: '340px', height: '400px' }}
        >
          {/* Ambient glow behind mascot */}
          <div
            className="absolute"
            style={{
              width: '280px',
              height: '280px',
              left: '30px',
              top: '40px',
              borderRadius: '50%',
              background: `radial-gradient(circle,
                rgba(139, 92, 246, ${glowIntensity * 0.15}) 0%,
                rgba(236, 72, 153, ${glowIntensity * 0.1}) 40%,
                transparent 70%
              )`,
              filter: 'blur(30px)',
            }}
          />

          {/* Body - Cute rounded robot body */}
          <div
            className="absolute"
            style={{
              width: '200px',
              height: '160px',
              left: '70px',
              top: '200px',
              borderRadius: '100px 100px 80px 80px',
              background: `linear-gradient(180deg,
                #f8fafc 0%,
                #e2e8f0 50%,
                #cbd5e1 100%
              )`,
              boxShadow: `
                0 20px 40px -10px rgba(0, 0, 0, 0.15),
                inset 0 -15px 30px -15px rgba(0, 0, 0, 0.1),
                inset 0 15px 30px -10px rgba(255, 255, 255, 0.9)
              `,
            }}
          >
            {/* Body shine */}
            <div
              className="absolute"
              style={{
                width: '80px',
                height: '40px',
                left: '60px',
                top: '15px',
                borderRadius: '50%',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, transparent 100%)',
              }}
            />

            {/* Chest light/core */}
            <div
              className="absolute"
              style={{
                width: '40px',
                height: '40px',
                left: '80px',
                top: '60px',
                borderRadius: '50%',
                background: `radial-gradient(circle at 40% 40%,
                  #a78bfa 0%,
                  #7c3aed 50%,
                  #5b21b6 100%
                )`,
                boxShadow: `
                  0 0 20px rgba(139, 92, 246, ${glowIntensity * 0.8}),
                  0 0 40px rgba(139, 92, 246, ${glowIntensity * 0.4}),
                  inset 0 -5px 15px rgba(0, 0, 0, 0.2),
                  inset 0 5px 10px rgba(255, 255, 255, 0.3)
                `,
              }}
            >
              {/* Core highlight */}
              <div
                className="absolute bg-white rounded-full"
                style={{
                  width: '10px',
                  height: '10px',
                  left: '10px',
                  top: '8px',
                  opacity: 0.7,
                }}
              />
            </div>
          </div>

          {/* Left Arm - Static */}
          <div
            className="absolute origin-top-right"
            style={{
              width: '35px',
              height: '90px',
              left: '40px',
              top: '220px',
              borderRadius: '20px',
              background: `linear-gradient(90deg, #cbd5e1 0%, #e2e8f0 50%, #f1f5f9 100%)`,
              boxShadow: 'inset -5px 0 15px rgba(0,0,0,0.1)',
              transform: 'rotate(10deg)',
            }}
          >
            {/* Hand */}
            <div
              className="absolute"
              style={{
                width: '30px',
                height: '30px',
                left: '2px',
                bottom: '-5px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)`,
                boxShadow: 'inset -3px -3px 8px rgba(0,0,0,0.1)',
              }}
            />
          </div>

          {/* Right Arm - Holding Magic Wand with arc motion */}
          <div
            className="absolute origin-top-left"
            style={{
              width: '35px',
              height: '90px',
              right: '40px',
              top: '220px',
              borderRadius: '20px',
              background: `linear-gradient(270deg, #cbd5e1 0%, #e2e8f0 50%, #f1f5f9 100%)`,
              boxShadow: 'inset 5px 0 15px rgba(0,0,0,0.1)',
              transform: `rotate(${-50 + wandAngle * 0.5}deg)`,
              transition: 'transform 0.1s ease-out',
            }}
          >
            {/* Hand */}
            <div
              className="absolute"
              style={{
                width: '30px',
                height: '30px',
                right: '2px',
                bottom: '-5px',
                borderRadius: '50%',
                background: `linear-gradient(225deg, #e2e8f0 0%, #cbd5e1 100%)`,
                boxShadow: 'inset 3px -3px 8px rgba(0,0,0,0.1)',
              }}
            />

            {/* Magic Wand */}
            <div
              className="absolute"
              style={{
                width: '8px',
                height: '80px',
                right: '-25px',
                bottom: '-40px',
                borderRadius: '4px',
                background: 'linear-gradient(90deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
                transform: `rotate(${-30 + wandAngle}deg)`,
                transformOrigin: 'top center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              {/* Wand tip - glowing star */}
              <div
                className="absolute"
                style={{
                  width: '20px',
                  height: '20px',
                  left: '-6px',
                  bottom: '-15px',
                  borderRadius: '50%',
                  background: `radial-gradient(circle at 40% 40%,
                    #fef08a 0%,
                    #fbbf24 50%,
                    #f59e0b 100%
                  )`,
                  boxShadow: `
                    0 0 15px rgba(251, 191, 36, ${glowIntensity}),
                    0 0 30px rgba(251, 191, 36, ${glowIntensity * 0.6}),
                    0 0 45px rgba(251, 191, 36, ${glowIntensity * 0.3})
                  `,
                }}
              >
                {/* Star sparkle inside */}
                <svg
                  className="absolute"
                  style={{ left: '2px', top: '2px' }}
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M10 0 L11 8 L20 10 L11 12 L10 20 L9 12 L0 10 L9 8 Z"
                    fill="white"
                    opacity="0.8"
                  />
                </svg>
              </div>

              {/* Wand grip rings */}
              <div
                className="absolute bg-amber-400"
                style={{
                  width: '10px',
                  height: '4px',
                  left: '-1px',
                  top: '5px',
                  borderRadius: '2px',
                }}
              />
              <div
                className="absolute bg-amber-400"
                style={{
                  width: '10px',
                  height: '4px',
                  left: '-1px',
                  top: '12px',
                  borderRadius: '2px',
                }}
              />
            </div>
          </div>

          {/* Head - Main sphere */}
          <div
            className="absolute"
            style={{
              width: '240px',
              height: '220px',
              left: '50px',
              top: '20px',
              borderRadius: '50% 50% 45% 45%',
              background: `
                radial-gradient(ellipse 60% 40% at 50% 15%, rgba(255,255,255,0.95) 0%, transparent 60%),
                linear-gradient(180deg, #ffffff 0%, #f1f5f9 40%, #e2e8f0 100%)
              `,
              boxShadow: `
                0 30px 60px -15px rgba(0, 0, 0, 0.2),
                0 15px 30px -10px rgba(0, 0, 0, 0.1),
                inset 0 -20px 40px -15px rgba(0, 0, 0, 0.08),
                inset 0 20px 40px -10px rgba(255, 255, 255, 1)
              `,
            }}
          />

          {/* Antenna */}
          <div
            className="absolute"
            style={{
              width: '6px',
              height: '35px',
              left: '167px',
              top: '-5px',
              background: 'linear-gradient(180deg, #94a3b8 0%, #cbd5e1 100%)',
              borderRadius: '3px',
            }}
          >
            {/* Antenna ball */}
            <div
              className="absolute"
              style={{
                width: '18px',
                height: '18px',
                left: '-6px',
                top: '-12px',
                borderRadius: '50%',
                background: `radial-gradient(circle at 40% 40%,
                  #f472b6 0%,
                  #ec4899 50%,
                  #db2777 100%
                )`,
                boxShadow: `
                  0 0 15px rgba(236, 72, 153, ${glowIntensity * 0.8}),
                  0 0 30px rgba(236, 72, 153, ${glowIntensity * 0.4}),
                  inset 0 3px 8px rgba(255, 255, 255, 0.4)
                `,
              }}
            />
          </div>

          {/* Face area background */}
          <div
            className="absolute"
            style={{
              width: '180px',
              height: '90px',
              left: '80px',
              top: '70px',
              borderRadius: '45px',
              background: 'linear-gradient(180deg, rgba(241, 245, 249, 0.5) 0%, rgba(226, 232, 240, 0.3) 100%)',
            }}
          />

          {/* Left Eye */}
          <div
            className="absolute"
            style={{
              width: '55px',
              height: blinkState ? '8px' : '55px',
              left: '95px',
              top: blinkState ? '95px' : '75px',
              borderRadius: blinkState ? '30px' : '50%',
              background: blinkState
                ? 'linear-gradient(180deg, #1e293b 0%, #334155 100%)'
                : `linear-gradient(135deg, #1e293b 0%, #0f172a 100%)`,
              boxShadow: blinkState
                ? 'none'
                : `inset 0 3px 10px rgba(0, 0, 0, 0.4)`,
              transition: 'all 0.08s ease-out',
              overflow: 'hidden',
            }}
          >
            {!blinkState && (
              <>
                {/* Iris */}
                <div
                  className="absolute rounded-full"
                  style={{
                    width: '38px',
                    height: '38px',
                    left: `${8 + eyePosition.x}px`,
                    top: `${8 + eyePosition.y}px`,
                    background: `radial-gradient(circle at 35% 35%,
                      #c084fc 0%,
                      #a855f7 30%,
                      #7c3aed 70%,
                      #5b21b6 100%
                    )`,
                    boxShadow: `
                      0 0 15px rgba(168, 85, 247, ${glowIntensity * 0.6}),
                      inset 0 2px 8px rgba(255, 255, 255, 0.3)
                    `,
                    transition: 'left 0.1s ease-out, top 0.1s ease-out',
                  }}
                >
                  {/* Pupil */}
                  <div
                    className="absolute rounded-full bg-black"
                    style={{
                      width: '16px',
                      height: '16px',
                      left: '11px',
                      top: '11px',
                    }}
                  />
                  {/* Eye sparkle */}
                  <div
                    className="absolute rounded-full bg-white"
                    style={{
                      width: '12px',
                      height: '12px',
                      left: '8px',
                      top: '6px',
                      opacity: 0.9,
                    }}
                  />
                  <div
                    className="absolute rounded-full bg-white"
                    style={{
                      width: '6px',
                      height: '6px',
                      left: '22px',
                      top: '20px',
                      opacity: 0.5,
                    }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Right Eye */}
          <div
            className="absolute"
            style={{
              width: '55px',
              height: blinkState ? '8px' : '55px',
              right: '95px',
              top: blinkState ? '95px' : '75px',
              borderRadius: blinkState ? '30px' : '50%',
              background: blinkState
                ? 'linear-gradient(180deg, #1e293b 0%, #334155 100%)'
                : `linear-gradient(135deg, #1e293b 0%, #0f172a 100%)`,
              boxShadow: blinkState
                ? 'none'
                : `inset 0 3px 10px rgba(0, 0, 0, 0.4)`,
              transition: 'all 0.08s ease-out',
              overflow: 'hidden',
            }}
          >
            {!blinkState && (
              <>
                {/* Iris */}
                <div
                  className="absolute rounded-full"
                  style={{
                    width: '38px',
                    height: '38px',
                    left: `${8 + eyePosition.x}px`,
                    top: `${8 + eyePosition.y}px`,
                    background: `radial-gradient(circle at 35% 35%,
                      #93c5fd 0%,
                      #60a5fa 30%,
                      #3b82f6 70%,
                      #2563eb 100%
                    )`,
                    boxShadow: `
                      0 0 15px rgba(59, 130, 246, ${glowIntensity * 0.6}),
                      inset 0 2px 8px rgba(255, 255, 255, 0.3)
                    `,
                    transition: 'left 0.1s ease-out, top 0.1s ease-out',
                  }}
                >
                  {/* Pupil */}
                  <div
                    className="absolute rounded-full bg-black"
                    style={{
                      width: '16px',
                      height: '16px',
                      left: '11px',
                      top: '11px',
                    }}
                  />
                  {/* Eye sparkle */}
                  <div
                    className="absolute rounded-full bg-white"
                    style={{
                      width: '12px',
                      height: '12px',
                      left: '8px',
                      top: '6px',
                      opacity: 0.9,
                    }}
                  />
                  <div
                    className="absolute rounded-full bg-white"
                    style={{
                      width: '6px',
                      height: '6px',
                      left: '22px',
                      top: '20px',
                      opacity: 0.5,
                    }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Cheek blush - Left */}
          <div
            className="absolute rounded-full"
            style={{
              width: '30px',
              height: '15px',
              left: '85px',
              top: '145px',
              background: 'radial-gradient(ellipse, rgba(251, 113, 133, 0.4) 0%, transparent 70%)',
            }}
          />

          {/* Cheek blush - Right */}
          <div
            className="absolute rounded-full"
            style={{
              width: '30px',
              height: '15px',
              right: '85px',
              top: '145px',
              background: 'radial-gradient(ellipse, rgba(251, 113, 133, 0.4) 0%, transparent 70%)',
            }}
          />

          {/* Mouth - Happy smile */}
          <div
            className="absolute"
            style={{
              width: '50px',
              height: '25px',
              left: '145px',
              top: '155px',
            }}
          >
            <svg width="50" height="25" viewBox="0 0 50 25" fill="none">
              <path
                d="M 5 5 Q 25 25 45 5"
                stroke="#475569"
                strokeWidth="4"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </div>

          {/* Ground shadow */}
          <div
            className="absolute"
            style={{
              width: '180px',
              height: '25px',
              left: '80px',
              bottom: '-15px',
              background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.15) 0%, transparent 70%)',
              borderRadius: '50%',
            }}
          />
        </div>
      </div>

      {/* Customer Ratings Section */}
      {showRatings && (
        <div
          className="flex items-center gap-3 px-5 py-2.5 bg-white rounded-full shadow-lg border border-gray-100"
          style={{ marginTop: '10px' }}
        >
          {/* Stacked Avatars */}
          <div className="flex -space-x-2.5">
            {customerAvatars.map((avatar, index) => (
              <div
                key={index}
                className="w-9 h-9 rounded-full border-2 border-white overflow-hidden shadow-sm"
                style={{ zIndex: customerAvatars.length - index }}
              >
                <img
                  src={avatar}
                  alt={`Customer ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          {/* Rating Text */}
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-gray-900 font-bold">4.9</span>
            <span className="text-gray-400">/5</span>
            <span className="text-gray-400 ml-0.5">from</span>
            <span className="text-gray-900 font-bold">4,268</span>
            <span className="text-gray-400">customers</span>
            {/* Star */}
            <svg
              className="w-4 h-4 text-amber-400 ml-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        </div>
      )}

      {/* CSS for sparkle animation */}
      <style>{`
        @keyframes sparkle-float {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-40px) scale(0.3);
          }
        }
      `}</style>
    </div>
  );
}
